/**
 * Ce qu'une série raconte, en aussi peu de caractères que possible.
 *
 * ⚠️ **Un fichier à part** : `react-refresh` interdit à un module de composants
 * d'exporter autre chose — la règle qui a déjà sorti `components/ui/tokens.ts`
 * de sa bibliothèque.
 */

import type { ActualSetDTO } from '../../../../shared/types';

/**
 * « 62.5 kg × 9 » — ce que la série a donné, pour la colonne « précédent ».
 *
 * 🔴 **La charge D'ABORD, et une forme courte** *(29/08/2026, après usage)*.
 * La première version écrivait « 9 reps · 62.5 kg · RPE 8 » : dans une colonne
 * qui fait ~115 px sur un écran de 375, elle se tronquait à « 9 reps · 6… » et
 * **la charge disparaissait** — c'est-à-dire très exactement l'information
 * qu'on vient chercher devant la barre. Ce n'était pas une donnée manquante,
 * c'était une donnée chassée par sa propre mise en forme.
 *
 * ⚠️ **L'ordre n'est pas cosmétique** : on se demande « j'avais mis combien ? »,
 * et le nombre de répétitions ne vient qu'ensuite pour dire si la charge avait
 * passé. Mettre les reps en tête, c'est répondre à la seconde question avant la
 * première.
 *
 * ⚠️ **Le RPE est REVENU** *(29/08/2026, après usage)*. Il avait été retiré au
 * tour précédent parce qu'il tenait la place de la charge : le retrait traitait
 * le bon symptôme au mauvais endroit — ce qui manquait de place, c'était la
 * **ligne**, pas le RPE. La ligne tient désormais sur deux rangs, l'info
 * dispose de toute la largeur, et il a retrouvé sa place.
 *
 * ⚠️ **`×` et non `x`** : c'est le signe multiplication (U+00D7), pas la lettre.
 * La différence se voit à l'œil sur un chiffre tabulaire.
 */
export function compactResult(set: ActualSetDTO): string {
  const corps = resultBody(set);
  const effort = effortLabel(set);
  return effort === null ? corps : `${corps} · ${effort}`;
}

/**
 * La charge et la mesure seules — « 62.5 kg × 9 », sans le RPE.
 *
 * 🔴 **Séparée du RPE le 06/09/2026**, parce que la colonne « précédent » les
 * met désormais sur **deux lignes** : « pour qu'on puisse tout voir sans que ce
 * soit tronqué ». Une seule chaîne aurait obligé l'écran à la recouper, donc à
 * réinterpréter un format — et deux découpages du même texte finissent par ne
 * plus tomber au même endroit.
 *
 * ⚠️ **`compactResult` la compose toujours** : la fiche d'un exercice liste
 * plusieurs séries sur une ligne, où la forme d'un seul tenant est la bonne. Une
 * définition, deux assemblages.
 */
export function resultBody(set: ActualSetDTO): string {
  /*
   * 🔴 **Une charge NULLE n'est pas une charge** *(07/09/2026, vu sur
   * l'appareil)* : un gainage au poids du corps affichait « 0 kg × 40 s ». Le
   * zéro venait d'une série écrite avec `weightKg: 0` plutôt que `null` — la
   * distinction n'a de sens ni pour qui lit, ni pour ce qu'on a soulevé.
   *
   * ⚠️ C'est déjà la règle de `heaviestWeightOf` et de `sessionVolumeOf`, qui
   * filtrent `kg > 0` : le rappel s'aligne dessus plutôt que d'avoir sa propre
   * idée de ce qu'est une charge.
   */
  const charge =
    set.weightKg === null || set.weightKg <= 0 ? null : `${String(set.weightKg)} kg`;

  const mesure =
    set.durationSeconds !== null
      ? `${String(set.durationSeconds)} s`
      : set.reps !== null
        ? String(set.reps)
        : null;


  const corps =
    charge !== null && mesure !== null ? `${charge} × ${mesure}` : (charge ?? mesure);
  return corps ?? '—';
}

/**
 * « RPE 8 », ou `null` si la série n'en portait pas.
 *
 * ⚠️ **Le RPE reste à part** : il n'est ni une charge ni une mesure, et c'est
 * lui qui cédait la place quand la colonne se tronquait — le défaut du 29/08,
 * puis à nouveau le 06/09 quand la ligne est passée sur une seule rangée.
 * Sur sa propre ligne, il ne dispute plus rien à personne.
 */
export function effortLabel(set: ActualSetDTO): string | null {
  return set.rpe === null ? null : `RPE ${String(set.rpe)}`;
}

/**
 * « 1:30 » — un repos, sous la forme d'un temps qui court.
 *
 * ⚠️ **Distinct de `formatSeconds` de `routineDraft.ts`, qui écrit
 * « 1 min 30 ».** Celui-là sert à *composer* une routine, où l'on raisonne en
 * minutes ; celui-ci accompagne un chronomètre, et doit se lire dans la même
 * unité que lui. Deux formes des mêmes secondes sur le même écran serait le
 * défaut ; ici elles ne s'y croisent pas.
 */
export function restLabel(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m === 0 ? `${String(s)} s` : `${String(m)}:${String(s).padStart(2, '0')}`;
}

